import type React from 'react';
import {
    AcademicCapIcon,
    BellIcon,
    BuildingOffice2Icon,
    CalendarIcon,
    CameraIcon,
    ChartBarIcon,
    CheckBadgeIcon,
    ClipboardDocumentCheckIcon,
    ClockIcon,
    FaceSmileIcon,
    GiftIcon,
    HeartIcon,
    LightBulbIcon,
    MegaphoneIcon,
    PresentationChartLineIcon,
    RocketLaunchIcon,
    SparklesIcon,
    StarIcon,
    TrophyIcon,
    UsersIcon,
    VideoCameraIcon,
    WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { CoffeeCupIcon, ToothIcon } from './eventIconGlyphs';
import type { EventIconProps } from './eventIconGlyphs';

// `iconKey` on `EventDto` is a key into this map; `null` means no icon badge.
// It no longer implies a centered banner - `alignment` carries the layout, and
// either alignment may render with or without an icon.
export const EVENT_ICONS: Record<string, React.FC<EventIconProps>> = {
    coffee: CoffeeCupIcon,
    calendar: CalendarIcon,
    tooth: ToothIcon,
    megaphone: MegaphoneIcon,
    sparkles: SparklesIcon,
    trophy: TrophyIcon,
    star: StarIcon,
    gift: GiftIcon,
    bell: BellIcon,
    heart: HeartIcon,
    smile: FaceSmileIcon,
    camera: CameraIcon,
    badge: CheckBadgeIcon,
    chart: ChartBarIcon,
    presentation: PresentationChartLineIcon,
    clipboard: ClipboardDocumentCheckIcon,
    clock: ClockIcon,
    users: UsersIcon,
    academic: AcademicCapIcon,
    lightbulb: LightBulbIcon,
    rocket: RocketLaunchIcon,
    video: VideoCameraIcon,
    practice: BuildingOffice2Icon,
    tools: WrenchScrewdriverIcon
};

// Human-facing labels for the admin icon picker. A key without an entry falls
// back to the raw key rather than rendering blank.
export const EVENT_ICON_LABELS: Record<string, string> = {
    coffee: 'Coffee cup',
    calendar: 'Calendar',
    tooth: 'Tooth',
    megaphone: 'Megaphone',
    sparkles: 'Sparkles',
    trophy: 'Trophy',
    star: 'Star',
    gift: 'Gift',
    bell: 'Bell',
    heart: 'Heart',
    smile: 'Smiley face',
    camera: 'Camera',
    badge: 'Verified badge',
    chart: 'Bar chart',
    presentation: 'Presentation',
    clipboard: 'Clipboard',
    clock: 'Clock',
    users: 'People',
    academic: 'Graduation cap',
    lightbulb: 'Light bulb',
    rocket: 'Rocket',
    video: 'Video camera',
    practice: 'Practice building',
    tools: 'Tools'
};

export function eventIconLabel(key: string): string {
    return EVENT_ICON_LABELS[key] ?? key;
}

// An unknown `iconKey` from the API must degrade gracefully rather than throw.
export function resolveEventIcon(key: string | null | undefined): React.FC<EventIconProps> | null {
    if (key && key in EVENT_ICONS) {
        return EVENT_ICONS[key];
    }
    return null;
}

export type { EventIconProps };
